import { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useLocation, useNavigate, useSearchParams, Location } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { log } from '@/logger/logger.ts';
import { TeamInviteSignin } from '@/components/Homepage/TeamInviteSignin';
import { AuthContext } from '@/contexts/AuthContext';
import { SERVER_ERROR_CODES } from '@/consts/server-error-codes.consts';
import { I18N_KEYS } from '@/language/keys';
import { getAxiosInstance } from '@/services/axiosConfig';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { color } from '@/colors';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { Copyrights } from '@/components/Common/Copyrights';

const logger = log.getLogger('TeamInviteActivationPage');
const axiosInstance = getAxiosInstance();

const useWrappedLocation = () => {
  const location = useLocation();
  return location as Location<{ from?: string }>;
};

export const ACTIVATION_QUERY_PARAMS_KEY = 'weavy_signin_activation_query_params';

export function TeamInviteActivationPage() {
  const { t: translate } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser, enrichUser, isSigningIn, signIn, isMemberInviteTokenValid, setIsMemberInviteTokenValid } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { track } = useAnalytics();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const sessionParams = localStorage.getItem(ACTIVATION_QUERY_PARAMS_KEY);
  const isAfterFigmaRedirect = !!code || !!sessionParams;
  const [email, setEmail] = useState<string | null>(searchParams.get('email'));
  const [token, setToken] = useState<string | null>(searchParams.get('token'));
  const [workspaceName, setWorkspaceName] = useState<string | null>(searchParams.get('workspaceName'));
  const [promotion, setPromotion] = useState<string | null>(searchParams.get('promotion'));
  const [paramsLoaded, setParamsLoaded] = useState(!sessionParams);
  const location = useWrappedLocation();
  const from: string = typeof location.state?.from === 'string' ? location.state.from : '/';
  const [acceptInvitationError, setAcceptInvitationError] = useState(false);
  useEffect(() => {
    // load state params from localStorage if coming back from figma redirect
    if (isAfterFigmaRedirect && sessionParams) {
      const storedParams = new URLSearchParams(sessionParams);
      setEmail(storedParams.get('email'));
      setToken(storedParams.get('token'));
      setWorkspaceName(storedParams.get('workspaceName'));
      setPromotion(storedParams.get('promotion'));
      localStorage.removeItem(ACTIVATION_QUERY_PARAMS_KEY);
      setParamsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAfterFigmaRedirect]);

  const validateToken = async () => {
    try {
      await axiosInstance.post(`/v1/workspaces/members/invitation/${token}/validate`);
      setIsMemberInviteTokenValid(true);
    } catch (error: any) {
      logger.error('Failed validating user token', error, {
        errorMessage: error.message || 'N/A',
        userId: currentUser?.uid,
      });
      setIsMemberInviteTokenValid(false);
      track('team_invite_activation_page_invalid_token', {}, TrackTypeEnum.BI);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !paramsLoaded || isMemberInviteTokenValid || acceptInvitationError) {
      return;
    }

    if (isAfterFigmaRedirect) {
      // set the token valid to true if coming back from figma redirect
      setIsMemberInviteTokenValid(true);
      return;
    }

    void validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, paramsLoaded, isAfterFigmaRedirect, isMemberInviteTokenValid, acceptInvitationError]);

  // Handle Figma OAuth redirect
  useEffect(() => {
    if (!paramsLoaded) {
      return;
    }
    const authorizationCode = searchParams.get('code');
    if (authorizationCode && !currentUser && !isSigningIn) {
      void signIn({
        route: from,
        providerType: 'figma',
        authorizationCode,
        promotion,
        teamInviteToken: token,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentUser, isSigningIn, signIn, token, paramsLoaded, promotion]);

  useEffect(() => {
    const acceptInvitation = async () => {
      try {
        setIsLoading(true);
        await axiosInstance.post(`/v1/workspaces/members/invitation/${token}/accept`);
        await enrichUser();
        track('team_invite_activation_page_accepted', {}, TrackTypeEnum.BI);
        navigate('/?acceptInvitation=true');
      } catch (error: any) {
        logger.error('Failed accepting user invitation', error, {
          errorMessage: error?.message || 'N/A',
          userId: currentUser?.uid,
        });
        setIsMemberInviteTokenValid(false);
        if (
          error?.response?.data?.internalErrorCode === SERVER_ERROR_CODES.EMAIL_MISMATCH_ERROR_CODE &&
          error?.response?.data?.message
        ) {
          setErrorMessage(error.response.data.message);
        }
        track('team_invite_activation_page_error', {}, TrackTypeEnum.BI);
        setIsLoading(false);
        setAcceptInvitationError(true);
      }
    };

    if (!paramsLoaded) {
      return;
    }
    if (!email || !token || !workspaceName) {
      track('team_invite_activation_page_invalid_url', {}, TrackTypeEnum.BI);
      navigate('/');
      return;
    }
    // call acceptInvitation immediately if user is already logged and if not after figma redirect
    if (currentUser && !isLoading && !isSigningIn && isMemberInviteTokenValid && !isAfterFigmaRedirect) {
      void acceptInvitation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentUser,
    isSigningIn,
    isMemberInviteTokenValid,
    isLoading,
    enrichUser,
    navigate,
    track,
    token,
    email,
    workspaceName,
    isAfterFigmaRedirect,
    paramsLoaded,
  ]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            py: 3,
            px: 3,
          }}
        >
          <WeavyLogoFilled title="Figma Weave Logo" style={{ width: 35, height: 35, color: color.White100 }} />
        </Box>
        {(isLoading || isSigningIn) && <CircularProgress />}
        {isMemberInviteTokenValid && !currentUser && !isLoading && !isSigningIn && (
          <>
            <Typography variant="h2">
              {translate(I18N_KEYS.SETTINGS.TEAM.ACTIVATION_PAGE.TITLE, { workspace_placeholder: workspaceName })}
            </Typography>
            <Typography variant="body-std-rg" sx={{ mt: 2, mb: 3 }}>
              {translate(I18N_KEYS.SETTINGS.TEAM.ACTIVATION_PAGE.SUBTITLE, { email_placeholder: email })}
            </Typography>
            <TeamInviteSignin fullScreen={false} teamInviteToken={token} hintEmail={email} />
          </>
        )}
        {!isMemberInviteTokenValid && !isLoading && (
          <>
            <Typography variant="h3">{translate(I18N_KEYS.SETTINGS.TEAM.ACTIVATION_PAGE.ERROR_TITLE)}</Typography>
            <Typography variant="body-std-md">
              {errorMessage || translate(I18N_KEYS.SETTINGS.TEAM.ACTIVATION_PAGE.ERROR_SUBTITLE)}
            </Typography>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          py: 3,
          px: 3,
        }}
      >
        <Copyrights textColor={color.White100} />
      </Box>
    </>
  );
}
